import type { PlayerPublic, RoomView } from "@shared/types";
import { ENGINES, MAX_NAME } from "@shared/room";
import { useEffect, useRef, useState } from "react";
import { Avatar } from "../../components/Avatar";
import { Btn } from "../../components/Button";
import { CodeTiles } from "../../components/CodeTiles";
import { Bot, Copy, Crown, Eye, LogOut, MonitorPlay, Pencil, Plus, Settings, Share2, Trash2, UserPlus, WifiOff } from "../../components/Icon";
import { AvatarGrid } from "../../components/IdentityForm";
import { QR } from "../../components/QR";
import { Sheet } from "../../components/Sheet";
import { toast } from "../../components/Toast";
import { gameMeta } from "../meta";
import { useI18n } from "../../i18n";
import { asset } from "../../lib/asset";
import { avatarVoice, play } from "../../lib/sfx";
import type { RoomClient } from "../../lib/room-client";
import type { LocalRoom } from "../../lib/local-room";
import { GuessLobbyCard } from "../guess/RoomSettings";
import { SettingsFor } from "./SettingsPanels";
import { useAct } from "./useRoom";

export function joinUrl(code: string) {
  const base = location.origin + asset("");
  return `${base.replace(/\/$/, "")}/j/${code}`;
}

export function Lobby({ client, view, tv }: { client: RoomClient; view: RoomView; tv?: boolean }) {
  const { d, f } = useI18n();
  const act = useAct(client);
  const g = gameMeta(view.game);
  const engine = ENGINES[view.game];
  const isHost = !!view.me?.isHost;
  const players = view.players.filter((p) => p.role === "player");
  const audience = view.players.filter((p) => p.role === "audience");
  const enough = players.length >= engine.minPlayers;
  const [sel, setSel] = useState<PlayerPublic | null>(null);
  const [showSettings, setShowSettings] = useState(false);
  const [leave, setLeave] = useState(false);
  const local = client.local;

  // a new face in the lobby announces itself with its avatar's voice
  const seen = useRef<Set<string> | null>(null);
  useEffect(() => {
    const ids = new Set(view.players.map((p) => p.id));
    if (seen.current && (isHost || tv || local)) {
      const fresh = view.players.filter((p) => !seen.current!.has(p.id));
      fresh.slice(0, 2).forEach((p, i) => avatarVoice(p.avatar, { delay: i * 0.5 }));
    }
    seen.current = ids;
  }, [view.players, isHost, tv, local]);

  const url = joinUrl(view.code);
  const share = async () => {
    const text = f(d.lobby.shareText, { game: d.games[view.game].name, code: view.code });
    if (navigator.share) {
      try {
        await navigator.share({ title: "HOTGAME", text, url });
        return;
      } catch {
        /* cancelled */
      }
    }
    await navigator.clipboard?.writeText(`${text}\n${url}`);
    toast(d.common.copied, "good");
  };

  return (
    <div className="shell grid gap-6 py-6 lg:grid-cols-[minmax(0,1fr)_380px] lg:gap-10 lg:py-10">
      <div className="min-w-0 space-y-6">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="display text-4xl sm:text-5xl" style={{ color: g.color }}>{d.games[view.game].name}</h1>
            <p className="mt-2 font-bold text-ink-2">{f(d.lobby.lang, { lang: d.common.lang[view.lang] })}</p>
          </div>
          {!tv && (
            <div className="flex gap-2">
              <button className="icon-btn" aria-label={d.lobby.settings} onClick={() => setShowSettings(true)}>
                <Settings size={21} />
              </button>
              <button className="icon-btn" aria-label={d.lobby.leave} onClick={() => setLeave(true)}>
                <LogOut size={21} />
              </button>
            </div>
          )}
        </div>

        <div className="surface p-5 sm:p-6">
          <div className="mb-4 flex items-center justify-between">
            <h2 className="text-xl font-extrabold">{f(d.lobby.players, { n: players.length })}</h2>
            <span className="font-bold text-ink-3">{players.length}/{engine.maxPlayers}</span>
          </div>
          <ul className="grid grid-cols-3 gap-3 sm:grid-cols-4 md:grid-cols-5">
            {players.map((p) => (
              <PlayerTile key={p.id} p={p} me={p.id === view.me?.id} onClick={() => (isHost || p.id === view.me?.id) && !tv && setSel(p)} />
            ))}
            {isHost && engine.supportsBots && players.length < engine.maxPlayers && !tv && (
              <li>
                <button className="tile grid h-full min-h-[132px] w-full place-items-center p-3 text-center font-bold text-ink-2" onClick={() => act({ t: "addBot" })}>
                  <span className="grid place-items-center gap-1">
                    <Bot size={30} />
                    {d.lobby.addBot}
                  </span>
                </button>
              </li>
            )}
            {local && !tv && players.length < engine.maxPlayers && <AddLocalSeat client={client as LocalRoom} />}
          </ul>
          {audience.length > 0 && (
            <p className="mt-5 flex items-center gap-2 font-bold text-ink-3">
              <Eye size={18} /> {f(d.lobby.audience, { n: audience.length })}: {audience.map((a) => a.name).join(", ")}
            </p>
          )}
        </div>

        {view.game === "guess" && (
          <div className="lg:hidden">
            <GuessLobbyCard settings={view.settings} lang={view.lang} isHost={isHost && !tv} onEdit={() => setShowSettings(true)} />
          </div>
        )}

        <div className="lg:hidden">
          <StartBlock view={view} enough={enough} isHost={isHost} onStart={() => act({ t: "start" })} tv={tv} />
        </div>

        <div className="surface hidden p-6 lg:block">
          <h2 className="mb-5 text-xl font-extrabold">{d.lobby.settings}</h2>
          {!isHost && <p className="mb-4 text-ink-3">{d.lobby.settingsHostOnly}</p>}
          <SettingsFor view={view} editable={isHost} set={(patch) => act({ t: "settings", settings: { ...view.settings, ...patch } })} />
        </div>
      </div>

      <aside className="space-y-6">
        {!local && (
          <div className="surface p-6 text-center">
            <p className="font-bold text-ink-3">{d.lobby.code}</p>
            <div className="mt-2 flex justify-center">
              <CodeTiles code={view.code} size={tv ? 76 : 54} />
            </div>
            <div className="mt-5 flex justify-center">
              <QR value={url} size={tv ? 260 : 190} label={d.lobby.scan} />
            </div>
            <p className="mt-3 text-sm font-semibold text-ink-3">{d.lobby.scan}</p>
            {!tv && (
              <div className="mt-5 grid grid-cols-2 gap-2">
                <Btn size="sm" variant="soft" icon={<Share2 size={17} />} onClick={share}>{d.common.share}</Btn>
                <Btn
                  size="sm"
                  variant="soft"
                  icon={<Copy size={17} />}
                  onClick={async () => {
                    await navigator.clipboard?.writeText(url);
                    toast(d.common.copied, "good");
                  }}
                >
                  {d.lobby.copyLink}
                </Btn>
                <a className="btn btn-sm btn-soft" href={`https://wa.me/?text=${encodeURIComponent(`${f(d.lobby.shareText, { game: d.games[view.game].name, code: view.code })} ${url}`)}`} target="_blank" rel="noreferrer">
                  {d.lobby.whatsapp}
                </a>
                <a className="btn btn-sm btn-soft" href={`https://t.me/share/url?url=${encodeURIComponent(url)}&text=${encodeURIComponent(f(d.lobby.shareText, { game: d.games[view.game].name, code: view.code }))}`} target="_blank" rel="noreferrer">
                  {d.lobby.telegram}
                </a>
                <a className="btn btn-sm btn-ghost col-span-2" href={`${asset("")}room/${view.code}?tv=1`} target="_blank" rel="noreferrer">
                  <MonitorPlay size={18} /> {d.lobby.tv}
                </a>
              </div>
            )}
          </div>
        )}
        <div className="hidden lg:block">
          <StartBlock view={view} enough={enough} isHost={isHost} onStart={() => act({ t: "start" })} tv={tv} />
        </div>
      </aside>

      <Sheet open={showSettings} onClose={() => setShowSettings(false)} title={d.lobby.settings}>
        {!isHost && <p className="mb-4 text-ink-3">{d.lobby.settingsHostOnly}</p>}
        <SettingsFor view={view} editable={isHost} set={(patch) => act({ t: "settings", settings: { ...view.settings, ...patch } })} />
        <div className="mt-6 border-t-0">
          {view.me && !local && (
            <Btn variant="soft" block onClick={() => act({ t: "role", role: view.me!.role === "player" ? "audience" : "player" })}>
              {view.me.role === "player" ? d.lobby.becomeAudience : d.lobby.becomePlayer}
            </Btn>
          )}
        </div>
      </Sheet>

      <PlayerSheet client={client} view={view} p={sel} onClose={() => setSel(null)} />

      <Sheet open={leave} onClose={() => setLeave(false)} title={d.lobby.leave} size="narrow">
        <p className="text-lg text-ink-2">{d.lobby.leaveConfirm}</p>
        <div className="mt-6 flex flex-col gap-3">
          <Btn
            block
            onClick={async () => {
              setLeave(false);
              await act({ t: "leave" }, true);
              location.assign(asset(""));
            }}
          >
            {d.lobby.leave}
          </Btn>
          <Btn block variant="soft" onClick={() => setLeave(false)}>{d.common.cancel}</Btn>
        </div>
      </Sheet>
    </div>
  );
}

function StartBlock({ view, enough, isHost, onStart, tv }: { view: RoomView; enough: boolean; isHost: boolean; onStart(): void; tv?: boolean }) {
  const { d, f } = useI18n();
  const g = gameMeta(view.game);
  const engine = ENGINES[view.game];
  if (tv || !isHost)
    return (
      <div className="surface p-6 text-center">
        <p className="text-lg font-bold text-ink-2">{d.lobby.waitingHost}</p>
      </div>
    );
  return (
    <div className="space-y-3">
      <button
        className="btn btn-lg btn-block"
        style={{ "--b": g.color, "--s": g.deep, "--i": g.ink } as React.CSSProperties}
        disabled={!enough}
        onClick={() => {
          play("go");
          onStart();
        }}
      >
        {d.lobby.start}
      </button>
      {!enough && <p className="text-center font-bold text-ink-3">{f(d.lobby.needPlayers, { n: engine.minPlayers })}</p>}
    </div>
  );
}

function PlayerTile({ p, me, onClick }: { p: PlayerPublic; me: boolean; onClick(): void }) {
  const { d } = useI18n();
  return (
    <li>
      <button
        className="tile flex h-full min-h-[132px] w-full flex-col items-center justify-center gap-2 p-3 text-center"
        style={me ? ({ "--b": "var(--color-ground-4)" } as React.CSSProperties) : undefined}
        onClick={onClick}
        aria-label={p.name}
      >
        <span className="relative">
          <Avatar index={p.avatar} color={p.color} size={68} mood="idle" dim={!p.connected && !p.isBot} />
          {p.isHost && (
            <span className="absolute -top-1 -right-1 grid h-7 w-7 place-items-center rounded-full bg-flame text-[#2a1200]" title={d.common.host}>
              <Crown size={16} />
            </span>
          )}
          {p.isBot && (
            <span className="absolute -right-1 -bottom-1 grid h-7 w-7 place-items-center rounded-full bg-ground-5" title={d.common.bot}>
              <Bot size={16} />
            </span>
          )}
          {!p.connected && !p.isBot && (
            <span className="absolute -bottom-1 -left-1 grid h-7 w-7 place-items-center rounded-full bg-ground-5" title={d.lobby.offline}>
              <WifiOff size={15} />
            </span>
          )}
        </span>
        <span className="w-full truncate font-extrabold">{p.name}</span>
        {me && <span className="-mt-1.5 text-xs font-bold text-ink-3">{d.common.you}</span>}
      </button>
    </li>
  );
}

function AddLocalSeat({ client }: { client: LocalRoom }) {
  const { d, f } = useI18n();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [avatar, setAvatar] = useState(() => Math.floor(Math.random() * 19));
  const n = client.core.players.length + 1;
  return (
    <li>
      <button className="tile grid h-full min-h-[132px] w-full place-items-center p-3 text-center font-bold text-ink-2" onClick={() => setOpen(true)}>
        <span className="grid place-items-center gap-1">
          <UserPlus size={30} />
          {d.local.addPlayer}
        </span>
      </button>
      <Sheet open={open} onClose={() => setOpen(false)} title={d.local.addPlayer}>
        <form
          className="space-y-5"
          onSubmit={(e) => {
            e.preventDefault();
            const nm = name.trim() || f(d.local.playerPh, { n });
            const err = client.addSeat(nm, avatar);
            if (err) toast((d.err as Record<string, string>)[err] ?? d.err.server, "bad");
            else {
              avatarVoice(avatar);
              setName("");
              setAvatar(Math.floor(Math.random() * 19));
              setOpen(false);
            }
          }}
        >
          <input className="field" maxLength={MAX_NAME} value={name} autoFocus placeholder={f(d.local.playerPh, { n })} onChange={(e) => setName(e.target.value)} />
          <AvatarGrid value={avatar} onPick={(i) => { setAvatar(i); avatarVoice(i); }} taken={client.core.players.map((p) => p.avatar)} />
          <Btn type="submit" block icon={<Plus size={20} />}>{d.local.addPlayer}</Btn>
        </form>
      </Sheet>
    </li>
  );
}

function PlayerSheet({ client, view, p, onClose }: { client: RoomClient; view: RoomView; p: PlayerPublic | null; onClose(): void }) {
  const { d } = useI18n();
  const act = useAct(client);
  const isMe = !!p && p.id === view.me?.id;
  const isHost = !!view.me?.isHost;
  const [name, setName] = useState("");
  const [avatar, setAvatar] = useState(0);
  useEffect(() => {
    if (p) {
      setName(p.name);
      setAvatar(p.avatar);
    }
  }, [p]);
  const local = client.local;
  const asTarget = (fn: () => Promise<unknown>) => async () => {
    await fn();
    onClose();
  };
  return (
    <Sheet open={!!p} onClose={onClose} title={p?.name}>
      {p && (
        <div className="space-y-5">
          {(isMe || local) && !p.isBot ? (
            <form
              className="space-y-4"
              onSubmit={async (e) => {
                e.preventDefault();
                if (local && client.actAs) {
                  await client.actAs(p.id, { t: "rename", name });
                  await client.actAs(p.id, { t: "avatar", avatar });
                } else {
                  if (name.trim() && name !== p.name) await act({ t: "rename", name });
                  if (avatar !== p.avatar) await act({ t: "avatar", avatar });
                }
                avatarVoice(avatar);
                onClose();
              }}
            >
              <label className="label" htmlFor="ps-name">{d.lobby.rename}</label>
              <input id="ps-name" className="field" maxLength={MAX_NAME} value={name} onChange={(e) => setName(e.target.value)} />
              <AvatarGrid value={avatar} onPick={(i) => { setAvatar(i); avatarVoice(i); }} taken={view.players.filter((x) => x.id !== p.id).map((x) => x.avatar)} />
              <Btn type="submit" block icon={<Pencil size={18} />}>{d.common.save}</Btn>
            </form>
          ) : null}
          {isHost && !isMe && (
            <div className="grid gap-3">
              {p.isBot ? (
                <Btn variant="soft" block icon={<Trash2 size={18} />} onClick={asTarget(() => act({ t: "removeBot", id: p.id }))}>{d.lobby.removeBot}</Btn>
              ) : (
                <>
                  {!local && <Btn variant="soft" block icon={<Crown size={18} />} onClick={asTarget(() => act({ t: "host", id: p.id }))}>{d.lobby.makeHost}</Btn>}
                  <Btn variant="soft" block icon={<Trash2 size={18} />} onClick={asTarget(() => act({ t: "kick", id: p.id }))}>{local ? d.local.remove : d.lobby.kick}</Btn>
                </>
              )}
            </div>
          )}
        </div>
      )}
    </Sheet>
  );
}
